import type { Pocket } from '@/api/useOrders/requests'
import type { Destination } from '@/utils/general.types'
import { useOrders } from '@api/useOrders'
import { NotificationMessage } from '@gravitate-js/excalibrr'
import { useLocalStorage } from '@gravitate-js/the-armory'
import { useLpConfig } from '@hooks/useLpConfig'
import dayjs from 'dayjs'
import { createContext, useContext, useEffect, useState } from 'react'
import {
  DEFAULT_SUPPLY_FILTER_STATE,
  SupplyOwnerMap,
  type SupplyFilterFlags,
  type SupplyOptionFilterOptions,
} from '../Panes/ControlPane/PaneComponents/SupplyFilters/types'
import { useDestinationsContext } from './DestinationsContext'
import { useDefaultSupplyOwnerMap } from './util'
import { ProductGroupsApiType } from './OrderContainersContext/ProductGroups/useProductGroups'

export type SupplyOptionSort = 'Best' | 'Cheapest' | 'Shortest' | null

type SupplyOptionPayload = {
  destinations: Destination[]
  pockets: Pocket[]
  filter: SupplyOptionFilterOptions & { price_options?: number }
  sort: SupplyOptionSort
  driver_schedule_id?: string | null
  order_id?: string | null
  needs_update?: boolean
  supply_owner_map: SupplyOwnerMap
  as_of: string
} & SupplyFilterFlags

type SupplyOptionContextType = {
  bestSupplyHash?: string
  selectedSupply?: Gravitate.SupplyOption
  supplyFilters?: SupplyOptionFilterOptions
  supplyOptions?: Gravitate.SupplyOption[]
  setPayload: (payload: SupplyOptionPayload) => void
  supplyOptionsPayload?: SupplyOptionPayload
  supplyLoading: boolean
  selectedSort: SupplyOptionSort
  selectedStatus: string
  nonOptimalChosen: boolean
  setNonOptimalChosen: React.Dispatch<React.SetStateAction<boolean>>
  initialSupplyFilters?: SupplyOptionFilterOptions
  reloadSupplyOptions: (
    order: Gravitate.Order,
    destinations: Destination[],
    pockets: Pocket[],
    sort: SupplyOptionSort,
    needsReload: boolean,
    filters: SupplyOptionFilterOptions,
    flags: SupplyFilterFlags,
    supplyOwnerMap: SupplyOwnerMap | undefined,
    productGroups: ProductGroupsApiType['productGroups'],
    isByProduct: boolean
  ) => void
  handleNewOptions: (
    order: Gravitate.Order,
    filters: SupplyOptionFilterOptions,
    options: Gravitate.SupplyOption[]
  ) => void
  setSelectedSupply: (supply: Gravitate.SupplyOption) => void
  setSupplyFilters: (filters: SupplyOptionFilterOptions) => void
  setInitialSupplyFilters: (filters: SupplyOptionFilterOptions) => void
  setSupplyOptions: (options: Gravitate.SupplyOption[]) => void
  setSupplyLoading: (loading: boolean) => void
  setSelectedSort: (sort: SupplyOptionSort) => void
  setSelectedStatus: (status: string) => void
  includeAlternates: boolean
  setIncludeAlternates: (includeAlternates: boolean) => void
  onlyInNetwork: boolean
  setOnlyInNetwork: (onlyInNetwork: boolean) => void
  onlySingleSupplier: boolean
  setOnlySingleSupplier: (onlySingleSupplier: boolean) => void
  excludeOutages: boolean
  setExcludeOutages: (excludeOutages: boolean) => void
  resetOnlySingleSupplierFilter?: () => void
  optionsQueryStatus?: string
}

const SupplyOptionContext = createContext<SupplyOptionContextType>({} as SupplyOptionContextType)

export function SupplyOptionContextProvider({ children }: { children: React.ReactNode }) {
  const [selectedSupply, setSelectedSupply] = useState()
  const [supplyFilters, setSupplyFilters] = useState<SupplyOptionFilterOptions>()
  const [initialSupplyFilters, setInitialSupplyFilters] = useState(DEFAULT_SUPPLY_FILTER_STATE)
  const [supplyOptions, setSupplyOptions] = useState<SupplyOptionContextType['supplyOptions']>()
  const [supplyLoading, setSupplyLoading] = useState(true)
  const [selectedSort, setSelectedSort] = useState<SupplyOptionContextType['selectedSort']>('Best')
  const [nonOptimalChosen, setNonOptimalChosen] = useState(false)
  const [selectedStatus, setSelectedStatus] = useState('selected')
  const [includeAlternates, setIncludeAlternates] = useState(false)
  const { value: excludeOutages, setValue: setExcludeOutages } = useLocalStorage(
    'supplyOptionFilters::excludeOutages',
    true
  )
  const [bestSupplyHash, setBestSupplyHash] = useState()
  const [optionsOrder, setOptionsOrder] = useState<Gravitate.Order>()
  const { orderDestinations } = useDestinationsContext()

  const { value: onlyInNetwork, setValue: setOnlyInNetwork } = useLocalStorage(
    'supplyOptionFilters::onlyInNetwork',
    false
  )

  const { single_supplier_per_load: ssplDefault } = useLpConfig().config
  const [onlySingleSupplier, setOnlySingleSupplier] = useState(false)
  useEffect(() => setOnlySingleSupplier(ssplDefault), [ssplDefault])
  const resetOnlySingleSupplierFilter = () => setOnlySingleSupplier(ssplDefault)

  const [payload, setPayload] = useState<SupplyOptionPayload>()

  const { useSupplyOptionChoicesQuery } = useOrders()
  const { data: supplyOptionsData, status: optionsQueryStatus } = useSupplyOptionChoicesQuery(payload)

  useEffect(() => {
    if (supplyOptionsData && optionsQueryStatus === 'success') {
      supplyOptionsData.data.length > 0 && setBestSupplyHash(supplyOptionsData.data[0].hash)
      handleNewOptions(optionsOrder, supplyOptionsData.options, supplyOptionsData.data)
    }
    if (optionsQueryStatus === 'error') {
      setSupplyLoading(false)
      NotificationMessage('Supply Options Failure', 'Getting supply options failed please check console logs')
    }
  }, [supplyOptionsData, optionsQueryStatus])

  const handleNewOptions = (
    order: Gravitate.Order,
    filters: SupplyOptionFilterOptions,
    options: Gravitate.SupplyOption[]
  ) => {
    !supplyFilters && setSupplyFilters(filters)
    const newSelectedOption = options.find((o) => o.selected)

    if (newSelectedOption) {
      const supplyIndex = options.findIndex((o) => o.selected)

      if (supplyIndex !== 0) {
        const priceDifference =
          newSelectedOption.delta_to_best?.toFixed(2) ||
          (newSelectedOption.landed_cost - options[0].landed_cost).toFixed(2)

        if (priceDifference > 300) {
          setSelectedStatus('error')
        } else {
          setSelectedStatus('warning')
        }
        newSelectedOption.priceDifference = `${priceDifference}`
      } else {
        setSelectedStatus('selected')
      }
      setSelectedSupply(newSelectedOption)
    } else if (order) {
      setSelectedStatus('invalid')
      setSelectedSupply(order.supply_option)
    }

    setSupplyOptions(options)
    setSupplyLoading(false)
  }

  const reloadSupplyOptions = async (
    order: Gravitate.Order,
    destinations: Destination[],
    pockets: Pocket[],
    sort: 'Best' | 'Cheapest' | null,
    needsReload: boolean,
    filters: SupplyOptionFilterOptions | undefined,
    flags: SupplyFilterFlags,
    supplyOwnerMap: SupplyOwnerMap | undefined,
    productGroups: ProductGroupsApiType['productGroups'],
    isByProduct: boolean
  ) => {
    if (!pockets?.length || pockets.includes(null)) return
    if (pockets.some((pocket) => !pocket?.targets?.length)) return

    setSupplyLoading(true)
    if (sort) setSelectedSort(sort)

    setPayload({
      destinations,
      pockets,
      filter: {
        ...DEFAULT_SUPPLY_FILTER_STATE,
        ...initialSupplyFilters,
        ...filters,
      },
      ...flags,
      sort: sort === 'Best' ? 'Best' : sort,
      driver_schedule_id: order?.assigned_driver_schedule_id ?? null,
      order_id: order?.id ?? null,
      needs_update: needsReload,
      supply_owner_map: supplyOwnerMap?.definitions?.length ? supplyOwnerMap : useDefaultSupplyOwnerMap(orderDestinations, pockets, productGroups, isByProduct),
      as_of: filters?.as_of
        ? dayjs(filters.as_of).toISOString()
        : (order?.estimated_load_date ?? dayjs().toISOString()),
    })
    setOptionsOrder(order)
  }

  return (
    <SupplyOptionContext.Provider
      value={{
        bestSupplyHash,
        selectedSupply,
        supplyFilters,
        supplyOptions,
        setPayload,
        supplyOptionsPayload: payload,
        supplyLoading,
        selectedSort,
        selectedStatus,
        nonOptimalChosen,
        initialSupplyFilters,
        reloadSupplyOptions,
        setNonOptimalChosen,
        handleNewOptions,
        setSelectedSupply,
        setSupplyFilters,
        setInitialSupplyFilters,
        setSupplyOptions,
        setSupplyLoading,
        setSelectedSort,
        setSelectedStatus,
        includeAlternates,
        setIncludeAlternates,
        onlyInNetwork,
        setOnlyInNetwork,
        onlySingleSupplier,
        setOnlySingleSupplier,
        resetOnlySingleSupplierFilter,
        optionsQueryStatus,
        excludeOutages,
        setExcludeOutages,
      }}
    >
      {children}
    </SupplyOptionContext.Provider>
  )
}

export const useSupplyOptionContext = () => {
  const context = useContext(SupplyOptionContext)
  if (context === undefined) {
    throw new Error('Context must be used within a Provider')
  }
  return context
}
